import { type ReactNode, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

interface RoleGateProps {
  /** One or more roles that are allowed to access the wrapped content.
   *  Widened to string[] for synthetic roles like "manager". */
  roles: string[];
  children: ReactNode;
  /** Where to redirect unauthorized users. A string value is used directly;
   *  a function receives the user's activeRole and returns the target path.
   *  Defaults to /dashboard. */
  redirectTo?: string | ((activeRole: string | null) => string);
}

/**
 * RoleGate guards a route by the user's active role.
 *
 * Multi-role auto-switch: when the active role doesn't match the route
 * but the user HOLDS one of the allowed roles in their `roles[]`, we
 * switch the active role instead of redirecting. This lets a user with
 * both `admin` and `owner` (or `tenant` + `owner`, like البتيري) click a
 * link from /portal/tenant to /portal/owner/documents without first
 * popping a portal switcher — the route itself signals which portal
 * they want.
 *
 * Genuinely unauthorized users (no allowed role in their list at all)
 * still get redirected to `redirectTo`. Fail-closed while auth loads.
 */
export function RoleGate({
  roles,
  children,
  redirectTo = "/dashboard",
}: RoleGateProps) {
  const {
    user,
    activeRole,
    roles: userRoles,
    setActiveRole,
    isLoading,
    isRefreshing,
  } = useAuth();

  // Does the user hold any role that this route accepts? Used to decide
  // auto-switch vs redirect when the active role doesn't match.
  const eligibleHeldRole = userRoles?.find((r) => roles.includes(r.role));
  const activeRoleMismatch =
    !!activeRole && !!user && !roles.includes(activeRole);

  // Auto-switch effect: fire a state update when we see a mismatch AND
  // the user holds an eligible role. The next render runs with the new
  // active role and the gate passes naturally.
  useEffect(() => {
    if (!isLoading && activeRoleMismatch && eligibleHeldRole) {
      setActiveRole(eligibleHeldRole.role);
    }
  }, [
    isLoading,
    activeRoleMismatch,
    eligibleHeldRole,
    setActiveRole,
  ]);

  // While loading OR a token refresh is in flight, WAIT — never redirect (esp.
  // to portalPathForRole(null) = /no-access) on a focused/stale tab before the
  // rotated token resolves the role. Hard-capped (AUTH_REFRESH_GRACE_MS).
  if (isLoading || isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Authenticated user whose role hasn't resolved yet → WAIT, don't deny.
  // `activeRole` is transiently null during a token refresh / cold reload:
  // fetchUserData bails on a recoverable roles-query failure with
  // isLoading=false but no role set (see useAuth: the "tab-return flicker"
  // bail). Redirecting here would bounce a valid user to portalPathForRole(null)
  // = /no-access, a public dead-end with no way back. Render the spinner instead
  // — the Nest refresh loop re-runs fetchUserData and resolves the role (→ the
  // page renders), or the session terminates (→ user becomes null → the parent
  // ProtectedRoute sends them to /auth). A genuinely roleless user is signed out
  // by fetchUserData, so this never spins forever.
  if (user && !activeRole) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Mismatch but the user holds an eligible role → show spinner while
  // the useEffect-driven setActiveRole flush propagates; the next render
  // will pass the role check naturally. Redirecting here instead would
  // bounce the user away from the page they explicitly navigated to.
  if (activeRoleMismatch && eligibleHeldRole) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const effectiveRedirect =
    typeof redirectTo === "function" ? redirectTo(activeRole) : redirectTo;

  if (!activeRole || !roles.includes(activeRole)) {
    return <Navigate to={effectiveRedirect} replace />;
  }

  return <>{children}</>;
}
